'use client';

import { useState, useEffect } from 'react';
import MenuCategory from './MenuCategory';
import ProductCard, { Product } from './ProductCard';
import OrderSummary, { CartItem } from './OrderSummary';
import CheckoutModal, { OrderDetails } from './CheckoutModal';

interface OrderPlacementInteractiveProps {
  products: Product[];
  categories: string[];
}

export default function OrderPlacementInteractive({
  products,
  categories,
}: OrderPlacementInteractiveProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const filteredProducts = products.filter(
    (product) => product.category === activeCategory
  );

  const handleAddToCart = (product: Product, quantity: number) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [
        ...prevItems,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          quantity,
        },
      ];
    });
  };

  const handleUpdateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(id);
      return;
    }
    setCartItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const handleRemoveItem = (id: string) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const handleCheckout = () => {
    setIsCheckoutOpen(true);
  };

  const handleSubmitOrder = (orderDetails: OrderDetails) => {
    console.log('Order submitted:', { orderDetails, cartItems });
    alert(
      `Order placed successfully!\n\nOrder Type: ${orderDetails.orderType}\nDelivery: ${orderDetails.deliveryMethod}\nTotal: $${calculateTotal().toFixed(2)}\n\nThank you for your order!`
    );
    setCartItems([]);
    setIsCheckoutOpen(false);
  };

  const calculateTotal = () => {
    const subtotal = cartItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    const tax = subtotal * 0.08;
    return subtotal + tax;
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-accent rounded-xl w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 bg-accent rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen pt-24 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Category Tabs */}
              <MenuCategory
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />

              {/* Product Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <OrderSummary
                cartItems={cartItems}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveItem={handleRemoveItem}
                onCheckout={handleCheckout}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        total={calculateTotal()}
        onSubmitOrder={handleSubmitOrder}
      />
    </>
  );
}